import { ref } from 'vue';
import { useAxios } from '@plugins/axios';
import { useSnackbar } from '@/composables/useSnackbar';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';

/** A download that needs a request body, e.g. a filter too long for a URL. */
type DownloadOptionsType = {
    method?: RequestMethodEnum.Get | RequestMethodEnum.Post;
    data?: unknown;
};

type UseFileDownloadReturnType = {
    isDownloading: boolean;
    downloadFile: (endpoint: string, filename?: string, options?: DownloadOptionsType) => Promise<void>;
};

export const useFileDownload = (): UseFileDownloadReturnType => {
    const isDownloading = ref(false);
    const { handleSnackbarError, showSnackbarMessage } = useSnackbar();

    const downloadFile = async (
        endpoint: string,
        filename?: string,
        options: DownloadOptionsType = {},
    ): Promise<void> => {
        isDownloading.value = true;

        try {
            const response =
                options.method === RequestMethodEnum.Post
                    ? await useAxios()[RequestMethodEnum.Post](endpoint, options.data, { responseType: 'blob' })
                    : await useAxios()[RequestMethodEnum.Get](endpoint, { responseType: 'blob' });

            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });

            let downloadFilename = filename;
            if (!downloadFilename) {
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
            }

            if (!downloadFilename) {
                downloadFilename = 'download-' + new Date().getTime();
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('File download error:', error);
            const message = handleSnackbarError('Download failed');
            showSnackbarMessage(message);
        } finally {
            isDownloading.value = false;
        }
    };

    return {
        isDownloading: isDownloading.value,
        downloadFile,
    };
};
